import React, { useEffect, useRef, useState } from 'react';
import { Bot, Loader2, X } from 'lucide-react';
import { normalizeMeetingUrl } from '../../../../src/shared/meetingLink';
import { t } from '../../lib/i18n';

interface SendBotDialogProps {
  isDarkMode: boolean;
  onClose: () => void;
  /** Başarılıysa çözülür; hata fırlatırsa mesaj pencerede gösterilir. */
  onSend: (meetingUrl: string, title: string) => Promise<void>;
}

/** Toplantı linki yapıştırıp Skribby botunu hemen göndermek için küçük pencere. */
export const SendBotDialog: React.FC<SendBotDialogProps> = ({ isDarkMode, onClose, onSend }) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const meetingUrl = normalizeMeetingUrl(url);
  const valid = meetingUrl !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!meetingUrl || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      await onSend(meetingUrl, title.trim());
      onClose();
    } catch (err) {
      setError((err as Error).message || t('Bot gönderilemedi.', 'Could not send the bot.'));
    } finally {
      setIsSending(false);
    }
  };

  const field = isDarkMode
    ? 'bg-[#19191C] border-[#3F3F45] text-neutral-200 focus:border-[#3AC8E0] placeholder:text-neutral-600'
    : 'bg-white border-neutral-200 text-neutral-800 focus:border-[#46CBE2] placeholder:text-neutral-400';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 md:px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className={`w-full md:max-w-md rounded-t-[26px] md:rounded-2xl border border-x-0 border-b-0 md:border p-5 pb-8 md:pb-5 space-y-4 shadow-xl animate-sheet-up md:animate-none ${
          isDarkMode ? 'bg-[#19191C] border-[#3F3F42] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
        }`}
      >
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-[14px] font-bold">
            <Bot className="w-4 h-4 text-[#46CBE2]" />
            {t('Toplantıya bot gönder', 'Send a bot to a meeting')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('Kapat', 'Close')} className="p-1 rounded text-neutral-500 hover:text-neutral-300">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[12.5px] text-neutral-500 leading-relaxed">
          {t(
            'Bot toplantıya katılır, kaydı ve transkripti alır. Toplantı bitince özet otomatik çıkarılır. Toplantı sahibinin botu içeri alması gerekebilir.',
            'The bot joins the meeting and captures the recording and transcript. A summary is generated when the meeting ends. The host may need to admit the bot.'
          )}
        </p>

        <label className="block space-y-1.5">
          <span className="text-[12px] font-medium text-neutral-400">{t('Toplantı linki', 'Meeting link')}</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('Bağlantı ya da Meet kodu: abc-defg-hij', 'Link or Meet code: abc-defg-hij')}
            className={`w-full px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${field}`}
          />
          {url.trim() && !valid && (
            <span className="text-[11.5px] text-amber-400 block">
              {t('Google Meet, Zoom veya Microsoft Teams bağlantısı ya da Meet kodu girin.', 'Enter a Google Meet, Zoom or Microsoft Teams link, or a Meet code.')}
            </span>
          )}
        </label>

        <label className="block space-y-1.5">
          <span className="text-[12px] font-medium text-neutral-400">{t('Başlık (isteğe bağlı)', 'Title (optional)')}</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('Haftalık ekip toplantısı', 'Weekly team meeting')}
            className={`w-full px-3 py-2 rounded-xl border text-[13px] outline-none transition-colors ${field}`}
          />
        </label>

        {error && <p className="text-[12px] text-rose-400">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-lg text-[13px] font-medium text-neutral-400 hover:text-neutral-200"
          >
            {t('Vazgeç', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={!valid || isSending}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:opacity-40 text-[#F0EEE6] text-[13px] font-semibold transition-colors"
          >
            {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            {t('Botu gönder', 'Send bot')}
          </button>
        </div>
      </form>
    </div>
  );
};
